<template>
  <v-wait for="isFetchingDatapoints">
    <core-spinner slot="waiting" />
    <title-bar
      :show-back="back"
      :show-info="hasAccessToKpis && !isBeton"
      @info="kpiDialog = true"
    >
      <ProjectSelector
        :disable-project="isProjectManager"
        class="col-auto mr-2"
        @project-change="mixin_onProjectChange"
      />
      <DateSelector class="col-3" />
    </title-bar>
    <div class="dashboard-scroll overflow-y-auto">
      <div
        class="grid-container"
        :class="{ 'expandable-card-container': isConcreteExpanded }"
      >
        <MapComponent
          class="map"
          :class="{ 'map--beton': isBeton }"
          :center="mixin_center"
          :datapoints="mixin_nonSectionDatapoints"
          :remove-outside-visible-bounds="false"
          :show-user-position="true"
          :section-datapoints="mixin_sectionDatapoints"
          @expand="expand"
        >
          <l-marker
            v-for="(point, index) in concreteMapPoints"
            :key="'concrete-' + index"
            :lat-lng="[point.lat, point.lng]"
          >
            <l-icon :icon-anchor="[8, 24]">
              <v-icon
                color="primary"
                small
              >
                $fas fa-tint
              </v-icon>
            </l-icon>
            <l-popup>
              <strong>
                {{
                  $t('views.site-technician.concrete.popup.title', {
                    number: point.sampleNumber,
                  })
                }}
              </strong>
              <div>
                {{ $t('views.site-technician.concrete.popup.invoice') }} :
                {{ display(point.invoice) }}
              </div>
              <div>
                {{ $t('views.site-technician.concrete.popup.air') }} :
                {{ display(point.air) }}
              </div>
              <div>
                {{ $t('views.site-technician.concrete.popup.slump') }} :
                {{ display(point.slump) }}
              </div>
              <div>
                {{ $t('views.site-technician.concrete.popup.temperature') }} :
                {{ display(point.temperature) }}
              </div>
            </l-popup>
          </l-marker>
        </MapComponent>
        <gauge-chart
          v-if="!isBeton"
          class="gauge1"
          :kpi="kpiScore('IQTTBMTC', 'daily', selectedDate, user || userId)"
          :title="$t('views.site-technician.charts.received')"
          :gauge-identifier="'gaugeIQTTBMTC'"
        />
        <gauge-chart
          v-if="!isBeton"
          class="gauge2"
          :kpi="fieldDecisionalKpi"
          :title="$t('views.site-technician.charts.decisional')"
          :gauge-identifier="'gaugeIQDDM'"
        />
        <BarChart
          v-if="!isBeton"
          class="barChart"
          :title="$t('views.dashboard.graph.title')"
          :subtitle="$t('views.dashboard.graph.subtitle')"
          :datapoints="mixin_compactions"
        />
        <TableDatapointComponent
          v-if="!isBeton"
          ref="tableListDatapoints"
          class="table"
          :items="mixin_nonSectionDatapoints.concat(mixin_sectionDatapoints)"
        />
        <v-dialog
          v-if="hasAccessToKpis && !isBeton"
          v-model="kpiDialog"
          scrollable
          max-width="750px"
        >
          <TableKpiComponent :items="userKpis" />
        </v-dialog>

        <v-card
          v-if="isBeton"
          outlined
          class="concrete-card d-flex flex-column"
          :class="{ 'expanded-card': isConcreteExpanded }"
        >
          <v-btn
            icon
            small
            absolute
            right
            top
            color="grey"
            class="concrete-expand"
            @click="isConcreteExpanded = !isConcreteExpanded"
          >
            <v-icon>
              {{
                isConcreteExpanded
                  ? '$fas fa-compress-alt'
                  : '$fas fa-expand-alt'
              }}
            </v-icon>
          </v-btn>
          <FreshConcrete />
        </v-card>
      </div>
    </div>
  </v-wait>
</template>

<script>
import GaugeChart from '../Charts/Gauge.vue'
import MapComponent from '../Charts/Maps/Widget.vue'
import TableDatapointComponent from '../Charts/Tables/Datapoint.vue'
import TableKpiComponent from '../Charts/Tables/Kpi.vue'
import BarChart from '../Charts/Bar.vue'
import TitleBar from '@/components/core/TitleBar'
import ProjectSelector from '../../core/ProjectSelector.vue'
import DateSelector from '../../core/DateSelector.vue'
import FreshConcrete from './FreshConcrete.vue'
import { LIcon, LMarker, LPopup } from 'vue2-leaflet'

import { mapState, mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import { mergeKpis } from '@/utils/kpi'
import projectsListMixin from '@/mixins/projectsListMixin'
import siteTechMixin from '@/mixins/siteTechMixin'
import compactionsMixin from '@/mixins/compactionsMixin'

export default {
  name: 'SiteTechnician',
  components: {
    BarChart,
    GaugeChart,
    MapComponent,
    TableDatapointComponent,
    TableKpiComponent,
    TitleBar,
    ProjectSelector,
    DateSelector,
    FreshConcrete,
    LIcon,
    LMarker,
    LPopup,
  },
  mixins: [projectsListMixin, siteTechMixin, compactionsMixin],
  props: {
    project: {
      type: String,
      default: '',
    },
    user: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      kpiDialog: false,
      isConcreteExpanded: false,
    }
  },
  computed: {
    ...mapState(['kpi', 'idproject', 'selectedDate']),
    ...mapState('chantier', ['pours']),
    ...mapGetters([
      'kpiScore',
      'userId',
      'hasAccessToKpis',
      'fieldDecisionalKpi',
      'lastProdKpiByNames',
    ]),
    back() {
      return Boolean(this.$route.query.showBack) || false
    },
    computedDatapoints() {
      return this.mixin_datapointsForDate
    },
    fieldDecisionalKpi() {
      return mergeKpis(this.lastProdKpiByNames(['IQDUG', 'IQDUM']))
    },
    userKpis() {
      return _.chain(this.kpi || [])
        .filter((k) => k.date === this.selectedDate && k.user === this.user)
        .sortBy(['version', 'type'])
        .value()
    },
    concreteMapPoints() {
      const points = []
      ;(this.pours || []).forEach((pour) => {
        ;(pour.placements || []).forEach((placement) => {
          ;(placement.samples || []).forEach((sample) => {
            const { latitude, longitude } = sample.location || {}
            if (latitude != null && longitude != null) {
              points.push({
                lat: latitude,
                lng: longitude,
                sampleNumber: sample.sampleNumber,
                invoice: placement.invoice,
                air: placement.airContent1,
                slump: placement.slump1,
                temperature: placement.temperature,
              })
            }
          })
        })
      })
      return points
    },
    isBeton() {
      return (this.pours || []).length > 0
    },
  },
  watch: {
    project() {
      if (this.project) this.loadConcrete()
    },
    mixin_nonSectionDatapoints(newPoints) {
      this.$root.$emit('weatherLocationUpdated', newPoints?.[0]?.location)
    },
  },
  async created() {
    this.$wait.start('isFetchingDatapoints')
    await Promise.all([
      this.getDatapoints(),
      this.getCurrentPosition(),
      this.loadConcrete(),
    ])
    this.startWatchingPosition()
    this.$wait.end('isFetchingDatapoints')
  },
  beforeDestroy() {
    this.stopWatchingPosition()
  },
  methods: {
    ...mapActions('app', [
      'getCurrentPosition',
      'startWatchingPosition',
      'stopWatchingPosition',
    ]),
    ...mapActions('chantier', ['fetchPours']),

    async loadConcrete() {
      if (!this.project) return
      this.$wait.start('isFetchingConcrete')
      try {
        await this.fetchPours({ project: this.project })
      } finally {
        this.$wait.end('isFetchingConcrete')
      }
    },

    display(value) {
      return value === null || value === undefined ? '—' : value
    },

    expand() {
      const newPath = `${this.$route.path}/details?project=${
        this.idproject
      }&user=${this.user || this.userId}`
      this.$router.push(newPath)
    },
  },
}
</script>

<style scoped>
.dashboard-scroll {
  flex: 1 1 auto;
  min-height: 0;
}

.map {
  grid-row: 1 / span 6;
  grid-column: 1 / span 10;
}

.map--beton {
  grid-row: 1 / span 6;
  grid-column: 1 / span 12;
}

.concrete-card {
  grid-row: 7 / span 6;
  grid-column: 1 / span 12;
  min-height: 0;
  position: relative;
}

.concrete-expand {
  top: 4px;
  right: 4px;
  z-index: 4;
}

.gauge1 {
  grid-row: 1 / span 3;
  grid-column: 11 / span 2;
}

.gauge2 {
  grid-row: 4 / span 3;
  grid-column: 11 / span 2;
}

.barChart {
  grid-row: 7 / span 6;
  grid-column: 1 / span 6;
}

.table {
  grid-row: 7 / span 6;
  grid-column: 7 / span 6;
}
</style>
